import React from 'react';
import { useSelector } from 'react-redux';
import {
  AppContent,
  ErrorBoundary,
  PostMainContent,
  ContentAndSidebar,
  ArticleTitle,
  ArticleMainImage,
  ArticleContent,
  ShareButton,
  Ad, Taboola, Sponsor,
} from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import { HeaderConnected } from '../../components/connectedComponents/HeaderConnected';
import { FooterConnected } from '../../components/connectedComponents/FooterConnected';
import { MainWrapperConnected } from '../../components/connectedComponents/MainWrapperConnected';
import {
  getBy,
  getUpdatedAtFormatted,
  getTitle,
  getBody,
  getShareConfig,
  getCover, getSponsor, getAuthors,
} from '../../store/template/postPage/postPage.selectors';
import { getAdSlotId, getCanonicalUrl, getSiteName, getTemplateName } from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';
import { PostSidebarConnected } from '../../components/connectedComponents/PostSidebarConnected';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { getShowTaboola } from '../../store/template/postPageWithTaboola/postPageWithTaboola.selectors';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';


interface PostPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const PostPageWithTaboolaComponent: React.FunctionComponent<PostPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPageWithTaboolaContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const PostPageWithTaboolaContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const title = useSelector(getTitle);
  const by = useSelector(getBy);
  const authors = useSelector(getAuthors);
  const updatedAtFormatted = useSelector(getUpdatedAtFormatted);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const showTaboola = useSelector(getShowTaboola);

  return (
    <React.Fragment>
      <PostMetaTags />
      <HeadScripts />
      <Theme>
        <AppContent>
          <HeaderConnected />
          <MainWrapperConnected configThemeKey="mainPostPage">
            <Ad id={topAdId} />
            <ContentAndSidebar>
              <PostMainContent>
                <header>
                  <ArticleTitle title={title} by={by} updatedAtFormatted={updatedAtFormatted} authors={authors}>
                    <ShareButton shareConfig={shareConfig} canonical={canonical} />
                  </ArticleTitle>
                  <ArticleMainImage {...cover} siteName={siteName} templateName={templateName} />
                </header>
                {sponsor ? <Sponsor {...sponsor} /> : null}
                <ArticleContent body={body} templateName={templateName} siteName={siteName} />
              </PostMainContent>
              <PostSidebarConnected adId1={sideBar1Id} adId2={sideBar2Id} />
              {showTaboola ? <Taboola /> : null}
            </ContentAndSidebar>
            <Ad id={oneOnOneId} />
          </MainWrapperConnected>
          <FooterConnected />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
